<template lang="pug">
.w-slider(
  v-hover-intent='hover'
  :class=`[ size, { hover: isHovered }, classes ]`
  @mousemove='seek'
  @mouseleave='unhover'
  @mousedown.prevent='drag'
  @mouseup.prevent='stopdrag'
)
  .content
    .track( ref='track' )
      .bar( ref='bar' :style=`{ width: barLength }`)
    .thumb( ref='thumb' :style=`{ left: thumbPos + 'px' }` )
      .inner
</template>

<script lang="js">
import { Clickable } from '@/mixins'
import { Logger } from '@/lib/logger'

export default {
  name: 'SliderWidget',
  mixins: [ Clickable ],
  components: {},
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    value: {
      type: Number,
      default: 0
    },
    size: {
      type: String,
      default: 'lg'
    },
    format: {
      type: Function,
      default: x => x
    },
    noLabels: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isHovered: false,
      hoverX: 0,
      track: null,
      trackHeight: 0,
      trackWidth: 0,
      dragging: false,
    }
  },
  created() {},
  mounted() {
    this.track = this.$refs.track
    this.trackHeight = this.track.offsetHeight
    this.trackWidth = this.track.offsetWidth
  },
  methods: {
    hover() {
      Logger.debug( 'hover()', '<w-slider>' )()
      this.isHovered = true
    },
    unhover() {
      this.isHovered = false
      setTimeout(() => {
        this.trackHeight = this.track.offsetHeight
        this.trackWidth = this.track.offsetWidth
      }, 500 )
    },
    seek( $event ) {
      this.trackHeight = this.track.offsetHeight
      this.trackWidth = this.track.offsetWidth
      this.hoverX = $event.layerX
      if ( this.dragging ) this.input()
    },
    drag() {
      this.dragging = true
    },
    stopdrag() {
      this.dragging = false
      this.input()
    },
    input() {
      const max = this.trackMax
      const min = this.trackMin
      const pos = this.thumbPos
      this.$emit(
        'input',
        this.min + (( pos - min ) / ( max - min ) * ( this.max - this.min ))
      )
    },
  },
  computed: {
    barLength() {
      const val = this.value < this.min ? this.min
            : this.value > this.max ? this.max : this.value
      return `calc(var(--h-slider-track) + calc(${
        Math.round( 1000 * val / (this.max - this.min )) / 1000
      } * calc( 100% - var(--h-slider-track))))`
    },
    trackMin() {
      return this.trackHeight / 2
    },
    trackMax() {
      return this.trackWidth - this.trackHeight / 2
    },
    thumbPos () {
      const x = this.hoverX
      const min = this.trackMin
      const max = this.trackMax
      const pos = x < min ? min : x > max ? max : x
      // const h = this.trackHeight
      // const w = this.trackWidth
      // Logger.debug( 'seek', '<w-slider>', { h, w, min, max, x, pos })()
      return pos
    }
  }
}
</script>

<style lang="scss">
.w-slider {
  &.xs2 {
    --s-font: var(--s-2xs);
    --h-widget: var(--s-2xs);
  }
  &.xs {
    --s-font: var(--s-2xs);
    --h-widget: var(--s-2xs);
  }
  &.sm {
    --s-font: var(--s-xs);
    --h-widget: var(--s-xs);
  }
  &.q3 {
    --s-font: var(--s-sm);
    --h-widget: var(--s-sm);
  }
  &.base {
    --s-font: var(--s-3q);
    --h-widget: var(--s-3q);
  }
  &.lg {
    --s-font: var(--s-base);
    --h-widget: var(--s-base);
  }
  &.lg2 {
    --s-font: var(--s-lg);
    --h-widget: var(--s-lg);
  }
  &.xl {
    --s-font: var(--s-2lg);
    --h-widget: var(--s-2lg);
  }
  &.xl2 {
    --s-font: var(--s-xl);
    --h-widget: var(--s-xl);
  }
  &.3xl {
    --s-font: var(--s-2xl);
    --h-widget: var(--s-2xl);
  }
  &.huge {
    --s-font: var(--s-3xl);
    --h-widget: var(--s-3xl);
  }
  --h-slider-track: var(--s-xs);
  --pad-slider-thumb: var(--s-sm);
  display: block;
  position: relative;
  height: var(--h-widget, var(--s-lg));
  padding: var(--pad-slider, var(--s-base));
  box-sizing: content-box;
  opacity: var(--opa-slider, 0.5);
  cursor: none;
  .content {
    position: relative;
    width: 100%;
    height: 100%;
    margin: 0 !important;
    padding: 0 !important;
  }
  .track, .bar, .thumb {
    position: absolute;
    border-radius: var(--h-widget, var(--s-lg));
    transition-property: height, width, opacity;
    transition-duration: 400ms;
  }
  .track {
    background: var(--bg-slider-track,
                    var(--bg-translucent,
                        rgba(255,255,255,0.15)));
    top: 50%;
    height: var(--h-slider-track);
    transform: translateY(-50%);
    width: 100%;
  }
  .bar {
    height: 100%;
    background: var(--bg-slider-bar, var(--c-fg-, white));
    .inner {
      transition-property: height, width, opacity;
      transition-duration: 400ms;
    }
  }
  .thumb {
    background: var(--bg-slider-thumb-outline, rgba(255,255,255,0.15));
    border-radius: 100%;
    height: calc( var(--h-slider-track) + 2 * var(--pad-slider-thumb) );
    width: calc( var(--h-slider-track) + 2 * var(--pad-slider-thumb) );
    padding: var(--pad-slider-thumb);
    top: 50%;
    transform: translate(-50%,-50%);
    transition-duration: 100ms;
    pointer-events: none;
    opacity: 0;
    .inner{
      background: var(--bg-slider-thumb, var(--c-fg));
      height: var(--h-slider-track);
      width: var(--h-slider-track);
      border-radius: 100%;
      pointer-events: none;
      box-shadow: var(--glow-active);
    }
  }
  &:hover, &.hover, &:focus, &.focus, &:active, &.active {
    opacity: var(--opa-slider-active, 0.85);
    .track, .bar, .thumb {
      --h-slider-track: var(--h-widget);
      transition-duration: 100ms;
    }
    .thumb {
      opacity: 1;
    }
  }
}
</style>
